import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import TargetSchoolFilterSelect from "./TargetSchoolFilterSelect";

/**
 * The filter row shared by the Mock Exams and Past Papers catalogues.
 *
 * Purely presentational — every page keeps its own state and passes it down. That
 * is what keeps the two pages identical: they used to carry near-duplicate filter
 * markup that drifted apart control by control.
 *
 * Three rows: search and the status toggle, then the dropdowns, then chips for
 * whatever is currently narrowing the list. A dropdown is hidden by omitting its
 * prop, so a page only shows the filters it actually has data for.
 */

function FilterSelect({ label, value, onChange, options, minWidth = 180, allLabel }) {
  const everythingLabel = allLabel || `All ${label}s`;
  return (
    <FormControl
      size="small"
      sx={{
        flex: { xs: "1 1 100%", sm: "0 0 auto" },
        minWidth: { sm: minWidth },
        maxWidth: { xs: "100%", sm: "none" },
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(val) =>
          options.find((o) => String(o.value) === String(val))?.label || everythingLabel
        }
      >
        <MenuItem value="">
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Radio size="small" checked={!value} />
          </ListItemIcon>
          {everythingLabel}
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Radio size="small" checked={String(value) === String(o.value)} />
            </ListItemIcon>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * Tester-only Active / Inactive filter. Inactive items only ever reach testers (the API
 * filters them out for everyone else), so pages pass `visibility` only for testers.
 * "" means both.
 */
export const VISIBILITY_DEFAULT = "active";
export const VISIBILITY_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/** Whether an item passes the visibility filter, given whether it is inactive. */
export function matchesVisibility(filter, isInactive) {
  if (filter === "active") return !isInactive;
  if (filter === "inactive") return Boolean(isInactive);
  return true;
}

export default function CatalogueFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  status,
  area,
  region,
  kind,
  category,
  schoolCategory,
  subject,
  year,
  visibility,
  schools = [],
  schoolValue = [],
  onSchoolChange,
  targetSchools = [],
  schoolLabelId,
  resultCount,
  totalCount,
  onClearAll,
}) {
  const schoolNameOf = (id) =>
    schools.find((s) => String(s.id) === String(id))?.name || `School ${id}`;

  const chips = [];
  if (search) chips.push({ key: "search", label: `Search: "${search}"`, clear: () => onSearchChange("") });
  if (status && status.value !== "all") {
    const label = status.options.find((o) => o.value === status.value)?.label;
    chips.push({ key: "status", label, clear: () => status.onChange("all") });
  }
  [
    ["area", "Exam / area", area],
    ["region", "Region", region],
    ["kind", "Paper type", kind],
    ["category", "Category", category],
    ["subject", "Subject", subject],
    ["year", year?.label || "Year", year],
  ].forEach(([key, name, control]) => {
    if (!control?.value) return;
    const label = control.options.find((o) => String(o.value) === String(control.value))?.label;
    chips.push({ key, label: `${name}: ${label || control.value}`, clear: () => control.onChange("") });
  });
  if (schoolCategory?.value && schoolCategory.value !== "independent") {
    const label = schoolCategory.options.find((o) => String(o.value) === String(schoolCategory.value))?.label;
    chips.push({
      key: "schoolCategory",
      label: `School type: ${label || schoolCategory.value}`,
      clear: () => schoolCategory.onChange("independent"),
    });
  }
  if (visibility && visibility.value !== VISIBILITY_DEFAULT) {
    const label = VISIBILITY_OPTIONS.find((o) => o.value === visibility.value)?.label || "Active + Inactive";
    chips.push({ key: "visibility", label: `Visibility: ${label}`, clear: () => visibility.onChange(VISIBILITY_DEFAULT) });
  }
  // Cap the school chips the same way the select's renderValue caps its summary.
  const schoolChips = (schoolValue || []).slice(0, 3);
  schoolChips.forEach((id) =>
    chips.push({
      key: `school-${id}`,
      label: schoolNameOf(id),
      clear: () => onSchoolChange((schoolValue || []).filter((s) => String(s) !== String(id))),
    })
  );
  if ((schoolValue || []).length > schoolChips.length) {
    chips.push({ key: "school-more", label: `+${schoolValue.length - schoolChips.length} schools` });
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 260px" }, minWidth: { sm: 220 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {status ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={status.value}
            aria-label="Show"
            // ToggleButtonGroup hands back null when the active button is clicked
            // again; without this guard the filter would silently become undefined.
            onChange={(_, next) => {
              if (next) status.onChange(next);
            }}
            sx={{
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5 },
            }}
          >
            {status.options.map((o) => (
              <ToggleButton key={o.value} value={o.value} aria-label={o.label}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        {area ? <FilterSelect label="Exam / area" minWidth={220} allLabel="All exams and areas" {...area} /> : null}
        {region ? <FilterSelect label="Region" minWidth={170} allLabel="All regions" {...region} /> : null}
        {kind ? <FilterSelect label="Paper type" minWidth={170} allLabel="All papers" {...kind} /> : null}
        {category ? <FilterSelect label="Category" minWidth={200} {...category} /> : null}
        {schoolCategory ? (
          <FilterSelect
            label="School type"
            minWidth={180}
            allLabel="All school types"
            {...schoolCategory}
          />
        ) : null}
        {subject ? <FilterSelect label="Subject" {...subject} /> : null}
        {year ? (
          <FilterSelect
            label={year.label || "Year"}
            minWidth={160}
            value={year.value}
            onChange={year.onChange}
            options={year.options}
          />
        ) : null}
        {visibility ? (
          <FilterSelect
            label="Visibility"
            minWidth={170}
            allLabel="Active + Inactive"
            value={visibility.value}
            onChange={visibility.onChange}
            options={VISIBILITY_OPTIONS}
          />
        ) : null}
        {schools.length > 0 ? (
          <TargetSchoolFilterSelect
            schools={schools}
            value={schoolValue}
            onChange={onSchoolChange}
            labelId={schoolLabelId}
            targetSchools={targetSchools}
            category={schoolCategory?.value || ""}
          />
        ) : null}
      </Box>

      {chips.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              variant="outlined"
              onDelete={chip.clear}
            />
          ))}
          <Button size="small" onClick={onClearAll} sx={{ textTransform: "none", fontWeight: 700 }}>
            Clear all
          </Button>
        </Stack>
      ) : null}

      {resultCount != null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing {resultCount}
          {totalCount != null && totalCount !== resultCount ? ` of ${totalCount}` : ""}
        </Typography>
      ) : null}
    </Box>
  );
}
